/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2024 Tech Inverge Solutions Pvt Ltd
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  ShoppingBag, 
  Users, 
  ShoppingBasket, 
  RefreshCw, 
  Clock, 
  ArrowRight,
  Loader
} from 'lucide-react';
import { buildApiUrl } from '../config';

interface Stats {
  totalSales: number;
  totalOrders: number;
  totalUsers: number;
  totalProducts: number;
}

interface RecentOrder {
  _id: string;
  razorpayOrderId?: string;
  paymentMethod?: 'Prepaid' | 'COD';
  amount: number;
  status: 'created' | 'placed' | 'paid' | 'failed' | 'cod_pending';
  createdAt: string;
  userId?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface RecentUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
}

const DashboardOverview: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ totalSales: 0, totalOrders: 0, totalUsers: 0, totalProducts: 0 });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      throw new Error('No token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    } as HeadersInit;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      throw new Error('Session expired');
    }

    return response;
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(buildApiUrl('/api/admin/stats'));
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
        setRecentOrders(data.recentOrders || []);
        setRecentUsers(data.recentUsers || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'placed':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'created':
      case 'cod_pending':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      
      {/* Title Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between pb-5 border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Dashboard Overview</h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time shop statistics, orders log, and recently registered customers
          </p>
        </div>
        <button
          onClick={loadStats}
          className="mt-4 md:mt-0 flex items-center px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl shadow-sm bg-white transition-all font-semibold text-xs"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Stats
        </button>
      </div>

      {loading && stats.totalOrders === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200/50 shadow-sm">
          <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
          <p className="text-gray-500 text-sm">Loading statistics and analytics...</p>
        </div>
      ) : (
        <>
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Revenue card */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg shadow-indigo-100 hover:scale-[1.02] transition-transform duration-200 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-indigo-100/90 block">Total Revenue</span>
                  <span className="text-3xl font-black mt-2 block">₹{stats.totalSales.toLocaleString('en-IN')}</span>
                </div>
                <div className="p-2.5 bg-white/10 rounded-xl border border-white/10">
                  <TrendingUp className="w-5 h-5" />
                </div>
              </div>
              <div className="text-xs text-indigo-100/80 flex items-center font-medium mt-4">
                Sum of all successful payments
              </div>
            </div>

            {/* Orders count */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-6 shadow-lg shadow-emerald-100 hover:scale-[1.02] transition-transform duration-200 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-100/90 block">Total Orders</span>
                  <span className="text-3xl font-black mt-2 block">{stats.totalOrders}</span>
                </div>
                <div className="p-2.5 bg-white/10 rounded-xl border border-white/10">
                  <ShoppingBag className="w-5 h-5" />
                </div>
              </div>
              <div className="text-xs text-emerald-100/80 flex items-center font-medium mt-4">
                Completed & pending checkout orders
              </div>
            </div>

            {/* Customers summary */}
            <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-2xl p-6 shadow-lg shadow-rose-100 hover:scale-[1.02] transition-transform duration-200 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-rose-100/90 block">Registered Customers</span>
                  <span className="text-3xl font-black mt-2 block">{stats.totalUsers}</span>
                </div>
                <div className="p-2.5 bg-white/10 rounded-xl border border-white/10">
                  <Users className="w-5 h-5" />
                </div>
              </div>
              <div className="text-xs text-rose-100/80 flex items-center font-medium mt-4">
                Registered profiles on store
              </div>
            </div>

            {/* Product summary */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-6 shadow-lg shadow-orange-100 hover:scale-[1.02] transition-transform duration-200 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-amber-100/90 block">Total Products</span>
                  <span className="text-3xl font-black mt-2 block">{stats.totalProducts}</span>
                </div>
                <div className="p-2.5 bg-white/10 rounded-xl border border-white/10">
                  <ShoppingBasket className="w-5 h-5" />
                </div>
              </div>
              <div className="text-xs text-amber-100/80 flex items-center font-medium mt-4">
                Items catalog length on portal
              </div>
            </div>

          </div>

          {/* Activity split layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Recent Orders log */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden lg:col-span-2">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-gray-900">Recent Customer Orders</h3>
                </div>
                <button
                  onClick={() => navigate('/admin/orders')}
                  className="inline-flex items-center text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  View All Orders
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </button>
              </div>
              
              <div className="divide-y divide-gray-100">
                {recentOrders.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No orders registered yet.</div>
                ) : (
                  recentOrders.map((order) => {
                    const date = new Date(order.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    });
                    
                    return (
                      <div key={order._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-all duration-150">
                        <div className="space-y-1">
                          <div className="font-bold text-gray-900 text-sm">{order.razorpayOrderId || order._id}</div>
                          <div className="text-xs text-gray-500">
                            {order.userId ? `${order.userId.firstName} ${order.userId.lastName}` : 'Guest'}
                            <span className="mx-1.5 text-gray-300">•</span>
                            {date}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="font-extrabold text-gray-900 text-sm">₹{order.amount.toFixed(2)}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 border rounded-full text-xs font-bold capitalize ${getStatusBadge(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent registered Users list */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <Users className="w-5 h-5 text-pink-600" />
                  <h3 className="font-bold text-gray-900">New Registered Users</h3>
                </div>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="inline-flex items-center text-xs font-bold text-pink-600 hover:text-pink-700 transition-colors"
                >
                  Manage
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </button>
              </div>

              <div className="divide-y divide-gray-100 px-6 py-2">
                {recentUsers.length === 0 ? (
                  <div className="py-6 text-center text-gray-500 text-sm">No registered customers.</div>
                ) : (
                  recentUsers.map((user) => {
                    const joined = new Date(user.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short'
                    });

                    return (
                      <div key={user._id} className="py-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-pink-50 border border-pink-100 text-pink-600 flex items-center justify-center font-bold text-xs">
                            {user.firstName[0]}
                          </div>
                          <div>
                            <span className="font-bold text-gray-900 text-sm block leading-tight">{user.firstName} {user.lastName}</span>
                            <span className="text-xs text-gray-400 block truncate w-36">{user.email}</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-md font-semibold">
                          {joined}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

          </div>
        </>
      )}

    </div>
  );
};

export default DashboardOverview;
